import { currentUserId } from '@/lib/session'
import { supabase } from '@/lib/supabase'

/* RLS does the authorization. These functions only shape the query — if a user
   is not a member of the pool, the select returns no rows and the caller
   redirects. Never add a client-side membership check on top; it drifts. */

/* tie_policy and lock_enabled are selected because the scoring disclaimer has to
   state THIS pool's rules. They are not cosmetic: a member who assumes ties are
   credited in a pool set to `no_credit` is wrong about their own record, and
   money settles off-platform on these standings.

   Deliberately still an explicit column list rather than `*`. RLS would allow a
   member to read every column of their own pool, which includes invite_code,
   entry_fee_amount, payout_structure and commissioner_venmo_handle — none of
   which belong in a browser that only needs to render a header and a rules
   panel. */
export async function fetchPoolBySlug(slug: string) {
  const { data, error } = await supabase
    .from('pools')
    .select('id, slug, name, season_id, tiebreaker_method_id, tie_policy, lock_enabled')
    .eq('slug', slug)
    .single()
  if (error) throw error
  return data
}

/* The shape actually fetched above — not `Tables<'pools'>`.
   PoolContext used to be typed as the full pools row while being populated from
   a five-column select, so `pool.tie_policy` type-checked and was undefined at
   runtime. Deriving the type from the function means the two can never drift:
   widen the select and this widens with it. */
export type PoolSummary = Awaited<ReturnType<typeof fetchPoolBySlug>>

/* The picker only needs enough to render a list and a link. */
export async function fetchMyPools() {
  const { data, error } = await supabase
    .from('pools')
    .select('id, slug, name, season_id, tiebreaker_method_id')
    .order('name')
  if (error) throw error
  return data
}

/* Reference data — 0006 grants anon select on tiebreaker_methods and the policy
   is `using (true)`, so this needs no membership. Fetched separately rather than
   embedded into fetchPoolBySlug: the only FK path from pools is
   pools_tiebreaker_method_id_fkey with isOneToOne false, so an embed would type
   as an array for what is a single row. */
export async function fetchTiebreakerMethod(methodId: string) {
  const { data, error } = await supabase
    .from('tiebreaker_methods')
    .select('id, name, description, requires_prediction, prediction_label')
    .eq('id', methodId)
    .single()
  if (error) throw error
  return data
}

/* Whether this member has accepted this pool's scoring terms, and which version.
   maybeSingle because "never acknowledged" is the normal first case, not an
   error. The user_id filter matches the composite primary key; RLS independently
   restricts this table to the caller's own rows. */
export async function fetchRulesAcknowledgement(poolId: string) {
  const userId = await currentUserId()

  const { data, error } = await supabase
    .from('pool_rules_acknowledgements')
    .select('pool_id, user_id, rules_version, acknowledged_at')
    .eq('pool_id', poolId)
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function acknowledgeRules(poolId: string, rulesVersion: string) {
  const userId = await currentUserId()

  // acknowledged_at is deliberately not sent — a trigger sets it server-side on
  // both insert and update, so the client cannot backdate a disclosure record.
  const { data, error } = await supabase
    .from('pool_rules_acknowledgements')
    .upsert(
      { pool_id: poolId, user_id: userId, rules_version: rulesVersion },
      // Matches the composite primary key (pool_id, user_id). Re-accepting
      // changed terms overwrites rules_version on the existing row.
      { onConflict: 'pool_id,user_id' }
    )
    .select()
    .single()
  if (error) throw error
  return data
}

/* seasons.id is an int (the calendar year, e.g. 2026), not a uuid. */
export async function fetchCurrentWeek(seasonId: number) {
  // The current week is the earliest one that has not locked yet; once the
  // season is over, fall back to the last week so the board still renders.
  //
  // `gt` on picks_lock_at also excludes NULL rows, which is what we want: the
  // playoff weeks have no schedule loaded yet, so they are not pickable.
  const { data, error } = await supabase
    .from('weeks')
    .select('id, season_id, week_number, label, picks_lock_at')
    .eq('season_id', seasonId)
    .gt('picks_lock_at', new Date().toISOString())
    .order('week_number')
    .limit(1)
    .maybeSingle()
  if (error) throw error
  if (data) return data

  const { data: last, error: lastError } = await supabase
    .from('weeks')
    .select('id, season_id, week_number, label, picks_lock_at')
    .eq('season_id', seasonId)
    .order('week_number', { ascending: false })
    .limit(1)
    .single()
  if (lastError) throw lastError
  return last
}
